<script setup lang="ts">
  import type { SliderRootProps, SliderRootEmits } from 'reka-ui'
  import type { CSSProperties, HTMLAttributes } from 'vue'
  import { SliderRoot, SliderTrack, SliderThumb, SliderRange, useForwardPropsEmits } from 'reka-ui'
  import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
  import { reactiveOmit, useElementSize } from '@vueuse/core'
  import { cn } from '@rapoo/element/lib/utils'
  import {
    sliderRangeVariants,
    sliderThumbVariants,
    sliderTrackVariants,
    sliderVariants,
    type SliderVariants,
  } from '.'

  const props = withDefaults(
    defineProps<
      SliderRootProps & {
        class?: HTMLAttributes['class']
        size?: SliderVariants['size']
        type?: SliderVariants['type']
        direction?: SliderVariants['direction']
        /** Range 终点相对 thumb 的视觉对齐方式。 */
        rangeAlignment?: 'track' | 'thumb-center' | 'thumb-end'
        /** 极值使用 Reka 原生空/满逻辑，或继续应用 thumb 对齐补偿。 */
        rangeEdgeBehavior?: 'reka' | 'aligned'
        /** 显式 thumb 主轴尺寸（px），同时用于视觉尺寸和 range 对齐计算。 */
        thumbSize?: number
        /** thumb 交叉轴尺寸，默认与 thumbSize 相同。 */
        thumbCrossSize?: number | string
        /** 从 min 动画到首个有效受控值，并为后续位置变化启用过渡。 */
        animated?: boolean
        trackClass?: HTMLAttributes['class']
        rangeClass?: HTMLAttributes['class']
        thumbClass?: HTMLAttributes['class']
      }
    >(),
    {
      size: 'xs',
      type: 'basic',
      direction: 'horizontal',
      rangeAlignment: 'track',
      rangeEdgeBehavior: 'reka',
      animated: false,
    },
  )

  const emits = defineEmits<SliderRootEmits>()

  const delegatedProps = reactiveOmit(
    props,
    'class',
    'size',
    'type',
    'direction',
    'rangeAlignment',
    'rangeEdgeBehavior',
    'thumbSize',
    'thumbCrossSize',
    'animated',
    'trackClass',
    'rangeClass',
    'thumbClass',
    'orientation',
  )

  const resolvedDirection = computed(() => props.direction ?? props.orientation ?? 'horizontal')

  const thumbRef = useTemplateRef<HTMLElement>('thumbRef')
  const { width: thumbWidth, height: thumbHeight } = useElementSize(thumbRef)

  const providedThumbSize = computed(() =>
    props.thumbSize !== undefined && props.thumbSize > 0 ? props.thumbSize : undefined,
  )
  const resolvedThumbSize = computed(
    () =>
      providedThumbSize.value ??
      (resolvedDirection.value === 'horizontal' ? thumbWidth.value : thumbHeight.value),
  )
  const thumbStyle = computed<CSSProperties | undefined>(() => {
    if (!providedThumbSize.value) return undefined

    const axisSize = `${providedThumbSize.value}px`
    const crossSize =
      typeof props.thumbCrossSize === 'number'
        ? `${props.thumbCrossSize}px`
        : (props.thumbCrossSize ?? axisSize)

    return resolvedDirection.value === 'horizontal'
      ? { width: axisSize, height: crossSize }
      : { width: crossSize, height: axisSize }
  })

  function cloneModelValue(values: number[] | null | undefined) {
    return Array.isArray(values) ? [...values] : values
  }

  function isSingleModelValue(values: number[] | null | undefined): values is number[] {
    return Array.isArray(values) && values.length === 1
  }

  const shouldWaitForInitialValue =
    props.animated && (props.modelValue == null || isSingleModelValue(props.modelValue))
  const isInitialAnimationPending = ref(shouldWaitForInitialValue)
  const isTransitionEnabled = ref(props.animated && !shouldWaitForInitialValue)
  const visualModelValue = ref<number[] | null | undefined>(
    shouldWaitForInitialValue ? [props.min ?? 0] : cloneModelValue(props.modelValue),
  )
  const positionTransitionClass =
    'transition-[left,right,top,bottom] duration-150 ease-out motion-reduce:transition-none'
  let isMounted = false
  let mountAnimationFrame: number | undefined
  let mountAnimationTargetFrame: number | undefined

  function cancelInitialAnimationFrames() {
    if (mountAnimationFrame !== undefined) cancelAnimationFrame(mountAnimationFrame)
    if (mountAnimationTargetFrame !== undefined) cancelAnimationFrame(mountAnimationTargetFrame)
    mountAnimationFrame = undefined
    mountAnimationTargetFrame = undefined
  }

  function scheduleInitialAnimation() {
    if (!isMounted || !isSingleModelValue(props.modelValue)) return

    cancelInitialAnimationFrames()
    // 第一帧只启用 transition，让 min 成为确定的已绘制起点；下一帧再切换真实值。
    mountAnimationFrame = requestAnimationFrame(() => {
      isTransitionEnabled.value = true
      mountAnimationTargetFrame = requestAnimationFrame(() => {
        if (!isSingleModelValue(props.modelValue)) return
        visualModelValue.value = cloneModelValue(props.modelValue)
        isInitialAnimationPending.value = false
      })
    })
  }

  watch(
    () => props.modelValue,
    (value) => {
      if (isInitialAnimationPending.value) {
        if (isSingleModelValue(value)) scheduleInitialAnimation()
        return
      }
      visualModelValue.value = cloneModelValue(value)
    },
    { deep: true },
  )

  onMounted(() => {
    isMounted = true
    if (isInitialAnimationPending.value) scheduleInitialAnimation()
  })

  onBeforeUnmount(() => {
    cancelInitialAnimationFrames()
  })

  /**
   * Reka 的 range 使用完整 track 百分比，而 contain thumb 在扣除自身尺寸后的区间内移动。
   * 这里优先使用显式尺寸保证首帧稳定，未提供时再通过 DOM 测量兼容自定义 thumb。
   * 多 thumb slider 的 range 同时存在起止边界，暂时保持 Reka 默认行为。
   */
  function getRangeStyle(values?: number[] | null): CSSProperties | undefined {
    if (props.rangeAlignment === 'track' || values?.length !== 1) return undefined

    const min = props.min ?? 0
    const max = props.max ?? 100
    const valueRange = max - min
    if (valueRange <= 0) return undefined

    const progress = Math.min(1, Math.max(0, (values[0] - min) / valueRange))
    const isEdge = progress === 0 || progress === 1
    if (isEdge && props.rangeEdgeBehavior === 'reka') return undefined

    const thumbSize = resolvedThumbSize.value
    if (!thumbSize) return undefined

    const isOverflow = props.thumbAlignment === 'overflow'
    const extension =
      props.rangeAlignment === 'thumb-end'
        ? thumbSize * (isOverflow ? 0.5 : 1 - progress)
        : isOverflow
          ? 0
          : thumbSize * (0.5 - progress)
    const rawEndPercent = (1 - progress) * 100
    const operator = extension >= 0 ? '-' : '+'
    const endEdge = getRangeEndEdge()

    return {
      [endEdge]: `calc(${rawEndPercent}% ${operator} ${Math.abs(extension)}px)`,
    }
  }

  function getRangeEndEdge(): 'top' | 'right' | 'bottom' | 'left' {
    if (resolvedDirection.value === 'vertical') return props.inverted ? 'bottom' : 'top'

    const documentDirection = typeof document === 'undefined' ? 'ltr' : document.dir || 'ltr'
    const direction = props.dir ?? documentDirection
    const startsFromLeft =
      (direction !== 'rtl' && !props.inverted) || (direction !== 'ltr' && props.inverted)
    return startsFromLeft ? 'right' : 'left'
  }

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <SliderRoot
    v-slot="{ modelValue }"
    v-bind="forwarded"
    :model-value="visualModelValue"
    :orientation="resolvedDirection"
    :class="
      cn(
        sliderVariants({ type: props.type, size: props.size, direction: resolvedDirection }),
        props.class,
      )
    "
  >
    <SliderTrack
      :class="cn(sliderTrackVariants({ direction: resolvedDirection }), props.trackClass)"
    >
      <SliderRange
        :class="
          cn(
            sliderRangeVariants({ direction: resolvedDirection }),
            isTransitionEnabled && positionTransitionClass,
            props.rangeClass,
          )
        "
        :style="getRangeStyle(modelValue)"
      />
    </SliderTrack>
    <SliderThumb
      ref="thumbRef"
      :class="
        cn(sliderThumbVariants(), isTransitionEnabled && positionTransitionClass, props.thumbClass)
      "
      :style="thumbStyle"
    >
      <slot name="thumb" />
    </SliderThumb>
  </SliderRoot>
</template>
